/**
 * Guest confirmation: human-readable VS/order# plus QR of the receipt viewUrl.
 * VS/order number alone is not an auth secret — the QR carries the recovery token.
 * S2-01 / G1 — never surface transaction.id as the customer-facing order code.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import type { GuestReceiptRecoveryDTO } from './checkoutApi.js';

export interface GuestReceiptRecoveryBlockProps {
  readonly recovery: GuestReceiptRecoveryDTO;
}

export function GuestReceiptRecoveryBlock({
  recovery,
}: GuestReceiptRecoveryBlockProps): JSX.Element {
  const { t } = useTranslation('customer');
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const viewUrl = recovery.viewUrl.trim();
  const variableSymbol = recovery.variableSymbol?.trim() ?? '';
  const orderReference = recovery.orderReference?.trim() ?? '';
  const guestAccessCode = recovery.guestAccessCode?.trim() ?? '';
  const transactionId = recovery.transactionId;
  const friendlyOrderReference =
    orderReference.length > 0 &&
    (transactionId == null || orderReference !== String(transactionId))
      ? orderReference
      : '';
  /** Order # row: friendly ref only; skip when it duplicates the VS row. */
  const showFriendlyOrderNumber =
    friendlyOrderReference.length > 0 && friendlyOrderReference !== variableSymbol;

  useEffect(() => {
    if (viewUrl.length === 0) {
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(viewUrl, {
      margin: 1,
      width: 220,
      errorCorrectionLevel: 'M',
    }).then((url) => {
      if (!cancelled) {
        setQrDataUrl(url);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [viewUrl]);

  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-[var(--color-border)] p-3"
      data-testid="guest-receipt-recovery-block"
    >
      <p className="text-sm font-medium text-[var(--color-on-surface)]">
        {t('receipt.recovery.title')}
      </p>
      <dl
        className="flex flex-col gap-1 text-sm"
        data-testid="guest-receipt-recovery-reference"
      >
        {showFriendlyOrderNumber ? (
          <div className="flex items-center justify-between gap-3">
            <dt className="text-[var(--color-on-surface-muted)]">
              {t('receipt.recovery.orderNumberLabel')}
            </dt>
            <dd className="text-[var(--color-on-surface)]">
              {t('receipt.recovery.orderNumber', { id: friendlyOrderReference })}
            </dd>
          </div>
        ) : null}
        {variableSymbol.length > 0 ? (
          <div className="flex items-center justify-between gap-3">
            <dt className="text-[var(--color-on-surface-muted)]">
              {t('receipt.recovery.variableSymbolLabel')}
            </dt>
            <dd className="text-[var(--color-on-surface)] font-mono">{variableSymbol}</dd>
          </div>
        ) : null}
        {guestAccessCode.length > 0 ? (
          <div className="flex items-center justify-between gap-3">
            <dt className="text-[var(--color-on-surface-muted)]">
              {t('receipt.recovery.guestAccessCodeLabel')}
            </dt>
            <dd
              className="font-mono text-[var(--color-on-surface)]"
              data-testid="guest-receipt-recovery-access-code"
            >
              {guestAccessCode}
            </dd>
          </div>
        ) : null}
      </dl>
      <p className="text-xs text-[var(--color-on-surface-muted)]">
        {t('receipt.recovery.vsInsufficient')}
      </p>
      {guestAccessCode.length > 0 ? (
        <p
          className="text-xs text-[var(--color-on-surface-muted)]"
          data-testid="guest-receipt-recovery-access-code-hint"
        >
          {t('receipt.recovery.guestAccessCodeHint')}
        </p>
      ) : null}
      <p
        className="text-xs text-[var(--color-on-surface-muted)]"
        data-testid="guest-receipt-recovery-non-fiscal"
      >
        {t('receipt.recovery.confirmationNonFiscal')}
      </p>
      {qrDataUrl !== null && viewUrl.length > 0 ? (
        <img
          src={qrDataUrl}
          alt={t('receipt.recovery.qrAlt')}
          className="mx-auto h-[220px] w-[220px] rounded-md border border-[var(--color-border)] bg-white p-2"
          data-testid="guest-receipt-recovery-qr"
        />
      ) : null}
      {viewUrl.length > 0 ? (
        <a
          className="text-xs text-[var(--color-accent)] underline break-all"
          href={viewUrl}
          rel="noopener noreferrer"
          data-testid="guest-receipt-recovery-open"
        >
          {t('receipt.recovery.openReceipt')}
        </a>
      ) : null}
    </div>
  );
}
